import React from 'react';
import {
  Checkbox,
  Flex,
  NumberField,
  Select,
  TagBadge,
  Text,
  TextField,
  type SelectOption,
} from '@chatsdet/ui';
import type { SandboxAction } from '../../../../features/sandboxes/hooks/useSandboxActions';

/** Values keyed by param name, typed exactly as the wire format the server checks. */
export type ActionParamsValues = Record<string, string | number | boolean>;

export interface ActionParamsEditorProps {
  schema: SandboxAction['inputSchema'];
  values: ActionParamsValues;
  onValuesChange: (values: ActionParamsValues) => void;
}

/** Update one key, dropping it entirely when cleared to an empty/undefined value. */
function setParam(
  values: ActionParamsValues,
  key: string,
  value: string | number | boolean | null
): ActionParamsValues {
  if (value === null || value === '') {
    const { [key]: _removed, ...rest } = values;
    return rest;
  }
  return { ...values, [key]: value };
}

/**
 * One declared param, rendered as the control its `type` calls for.
 *
 * @remarks There is deliberately no free-text key/value row: the server
 * validates params against the action's `inputSchema` and rejects unknown
 * keys, so a free-form editor would let the customer build a request that is
 * guaranteed to fail. Every field here comes from `schema.properties` — the
 * schema IS the form.
 */
function ParamField({
  paramKey,
  prop,
  required,
  value,
  onChange,
}: {
  paramKey: string;
  prop: SandboxAction['inputSchema']['properties'][string];
  required: boolean;
  value: string | number | boolean | undefined;
  onChange: (value: string | number | boolean | null) => void;
}) {
  if (prop.sensitive) {
    return (
      <TextField
        aria-label={paramKey}
        value=""
        isDisabled
        placeholder="Not supported here"
        description="Secrets are not supported here — this column is stored in plaintext. Supply them through the environment variable mapping instead."
      />
    );
  }

  if (prop.enum) {
    const options: SelectOption[] = prop.enum.map((option) => ({ id: option, label: option }));
    return (
      <Select
        aria-label={paramKey}
        items={options}
        placeholder={prop.default !== undefined ? `Default: ${String(prop.default)}` : 'Choose a value'}
        selectedKey={typeof value === 'string' ? value : null}
        onSelectionChange={(key) => onChange(String(key))}
        isRequired={required}
      />
    );
  }

  if (prop.type === 'boolean') {
    return (
      <Checkbox
        isSelected={value === true}
        onChange={(isSelected) => onChange(isSelected)}
        aria-label={paramKey}
        description='Arrives at the script as the string "true" or "false" — a shell test like [ -n "$INPUT_X" ] is true either way.'
      />
    );
  }

  if (prop.type === 'number' || prop.type === 'integer') {
    return (
      <NumberField
        aria-label={paramKey}
        value={typeof value === 'number' ? value : null}
        onChange={(next) => onChange(next)}
        isRequired={required}
      />
    );
  }

  return (
    <TextField
      aria-label={paramKey}
      value={typeof value === 'string' ? value : ''}
      onChange={(next) => onChange(next)}
      isRequired={required}
      minLength={prop.minLength}
    />
  );
}

/** Params for the chosen sandbox action, one row per declared schema property. */
export function ActionParamsEditor({ schema, values, onValuesChange }: ActionParamsEditorProps) {
  const properties = Object.entries(schema.properties);
  const required = new Set(schema.required ?? []);

  if (properties.length === 0) {
    return (
      <Text size="xs" className="text-muted-foreground">
        This action takes no parameters.
      </Text>
    );
  }

  return (
    <Flex direction="column" gap="sm">
      {properties.map(([key, prop]) => (
        <div
          key={key}
          className="grid grid-cols-1 items-start gap-2 rounded-md border border-border bg-muted/20 p-2.5 md:grid-cols-[minmax(0,14rem)_minmax(0,1fr)]"
        >
          <Flex direction="column" gap="xs">
            <Flex align="center" gap="xs" wrap>
              <Text size="sm" weight="medium">
                {key}
              </Text>
              {required.has(key) && (
                <TagBadge name="Required" color="blue" size="sm" hideDot />
              )}
              {prop.sensitive && (
                <TagBadge
                  name="Sensitive"
                  color="orange"
                  size="sm"
                  hideDot
                  tooltip="Not editable here — map a secret through environment variables instead"
                />
              )}
            </Flex>
            {prop.description && (
              <Text size="xs" className="text-muted-foreground">
                {prop.description}
              </Text>
            )}
          </Flex>
          <ParamField
            paramKey={key}
            prop={prop}
            required={required.has(key)}
            value={values[key]}
            onChange={(value) => onValuesChange(setParam(values, key, value))}
          />
        </div>
      ))}
    </Flex>
  );
}
